import {AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, Injectable, Input, signal} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatAnchor, MatButton} from "@angular/material/button";
import {AvtaleId, AvtaleModell, AvtaleRessursClient, BrukerId, EnkelGruppeModell, GruppeId, SlettGruppeResponsModell, SlettGruppeSpesifikasjon} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {Temporal} from "temporal-polyfill";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {OtpSkjemaKomponent} from "web-ng2/delt/komponenter/skjema/otp-felt/otp-felt";
import {lesZonedDateTimeSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {MatIcon} from "@angular/material/icon";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {NgClass} from "@angular/common";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {uriEnkodet} from "felles/stoettefunk/uri";
import {Filer} from "felles/tjenester/Filer";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {Dialog} from "felles-ng2/tjenester/Dialog";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Observable} from "rxjs";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import ZonedDateTime = Temporal.ZonedDateTime;

@Component({
    selector: 'slett-gruppe',
    templateUrl: './slett-gruppe.html',
    styleUrls: ['./slett-gruppe.scss'],
    imports: [
        TranslateModule,
        FormsModule,
        MatAnchor,
        ReactiveFormsModule,
        FormaterZonedDateTimePipe,
        MatButton,
        OtpSkjemaKomponent,
        MatIcon,
        NgClass
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SlettGruppeKomponent implements AfterViewInit {
    static ajsSelector = 'slettGruppe';


    gruppeSomSignal = signal<EnkelGruppeModell | null>(null);
    otpKodeSkjema = new FormControl('', {validators: Validators.required});
    otpValideringFeilet = signal(false);
    zipNedlastetTidspunkt = computed<ZonedDateTime | null>(() => {
          if (erNil(this.gruppeSomSignal())) return null;
          return lesZonedDateTimeSerialisertOpt(this.gruppeSomSignal()!.nedlastningsTidspunkt);
      }
    );

    zipNedlastingErStartet = computed(() => erIkkeNil(this.zipNedlastetTidspunkt()));

    lagrer = asynkArbeidPågårSignal();
    kanSletteGruppe = computed(() => {
        if (erNil(this.avtale())) return false;
        return this.avhengigheter.autentisertBrukersId() === this.avtale()!.kontaktperson.id
    });

    avtale = signal<AvtaleModell | null>(null);


    //ikke def som input() da dette ikke fungerer når komponent kalles fra ajs
    @Input({required: true}) set gruppe(gruppe: EnkelGruppeModell) {
        this.gruppeSomSignal.set(gruppe);
    }

    constructor(private destroyRef: DestroyRef, private avhengigheter: SlettGruppeKomponentAvhengigheter) {
        effect(() => {
            if (this.zipNedlastingErStartet()) {
                this.otpKodeSkjema.enable();
            } else {
                this.otpKodeSkjema.disable();
            }
        });
        effect(() => {
            if (erNil(this.gruppeSomSignal())) return;
            this.avhengigheter.hentAvtale(this.gruppeSomSignal()!.avtaleId)
              .then(avtale => {
                  this.avtale.set(avtale)
              });
        });
    }

    ngAfterViewInit(): void {
        //kan ikke gjøres i konstruktør da gruppeId da enda ikke er klar for å levere verdier
        this.avhengigheter.hentGruppe(this.gruppeSomSignal()!.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(gruppe => {
              this.gruppeSomSignal.set(gruppe);
          });
    }

    lastNedKlikket() {
        this.visNedlastningStartetDialog();
        // vent halvt sekund - last gruppe på nytt - da er nedlastningsTidspunkt satt
        setTimeout(() => {
            this.avhengigheter.hentGruppe(this.gruppeSomSignal()!.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(g => {
                this.gruppeSomSignal.set(g);
            })
        }, 500);
    }

    url() {
        return this.avhengigheter.serviceRotUrl() + "/api/gruppe/" + this.gruppeSomSignal()!.id + "/datadump/" + this.filnavn();
    }

    filnavn() {
        return uriEnkodet(Filer.somSikkertFilnavn(this.gruppeSomSignal()!.tittel)) + ".zip";
    }

    visNedlastningStartetDialog() {
        this.avhengigheter.bekreftNedlastningDialog();
    }

    async slett() {
        await this.lagrer.utfør(() => {
            return this.avhengigheter.slett(this.gruppeSomSignal()!.id, {verifiseringskode: this.otpKodeSkjema.value!})
        })
          .then(responseModell => {
              if (!responseModell.valideringOk) {
                  this.otpValideringFeilet.set(true);
              } else {
                  // er slettet - hva skjer med gruppe og siden?
                  this.otpValideringFeilet.set(false);
              }
              if (responseModell.slettet) {
                  this.avhengigheter.gåTilGruppeOversikt();
              }
          })

    }

}

@Injectable()
export class SlettGruppeKomponentAvhengigheter {

    constructor(private konfigurasjon: Appkonfigurasjon, private gruppeDatatjeneste: GruppeDatatjeneste, private navigasjon: Navigasjon, private avtaleRessurs: AvtaleRessursClient, private kontekstTilbyder: BrukerkontekstTilbyder, private dialog: Dialog, private oversettelser: Oversettelser) {
    }

    serviceRotUrl() {
        return this.konfigurasjon.serviceRotUrl;
    }

    slett(gruppeId: GruppeId, spesifikasjon: SlettGruppeSpesifikasjon): Promise<SlettGruppeResponsModell> {
        return this.gruppeDatatjeneste.slett(gruppeId, spesifikasjon);
    }

    gåTilGruppeOversikt() {
        this.navigasjon.gåTilGrupper();
    }

    hentGruppe(gruppeId: GruppeId): Observable<GruppeModellDML> {
        return this.gruppeDatatjeneste.hent(gruppeId);
    }

    hentAvtale(avtaleId: AvtaleId): Promise<AvtaleModell> {
        return this.avtaleRessurs.hentEnkel(avtaleId).somPromiseVisMeldingVedFeil(() => "FEIL!!");
    }

    autentisertBrukersId(): BrukerId {
        return this.kontekstTilbyder.autentisertBruker().bruker.id;
    }

    bekreftNedlastningDialog() {
        this.dialog.advarsel({tittel: this.oversettelser.oversett(i18n('Nedlastning startet')), innhold: this.oversettelser.oversett(i18n('Nedlastning har startet, den tar som regel noen minutter. Se i nettleserens nedlastninger.')), droppAvbrytKnapp: true});
    }

}
